/**
 * Color utility constants
 * Color palettes for tags and projects
 * No dependencies
 */

/**
 * Tag color palette - darker, high-contrast colors for white text legibility
 * Each color is carefully selected for accessibility and readability
 */
export const TAG_COLORS = [
    // Interleaved by hue family so adjacent indices are always visually distinct.
    // Prevents similar-looking colors when two tag names hash to nearby indices.
    '#dc2626', // red-600
    '#16a34a', // green-600
    '#4338ca', // indigo-700
    '#ea580c', // orange-600
    '#059669', // emerald-600
    '#7c3aed', // violet-600
    '#b45309', // amber-700
    '#0284c7', // blue-600   ← index 7: JULIO keeps its familiar blue
    '#6b21a8', // purple-800 ← index 8: collision bump lands here (very distinct)
    '#ca8a04', // yellow-700 (darker)
    '#0ea5a4', // teal-500
    '#be185d', // pink-600
    '#065f46', // emerald-800 (deep)
    '#e11d48', // rose-600
    '#64748b', // slate-500 neutral (avoid near-black tags)
    '#0369a1', // sky-700
];

/**
 * Project color palette - optimized for dark mode with white text
 * Each color provides good contrast against dark backgrounds
 */
export const PROJECT_COLORS = [
    '#6C5CE7', // Purple - good contrast
    '#3742FA', // Indigo - good contrast
    '#E84393', // Pink - good contrast
    '#00B894', // Teal - good contrast
    '#1565c0', // Blue-800 - dark blue, WCAG AA with white text
    '#0984E3', // Blue - better contrast than light blue
    '#0891b2', // Cyan-600 - darker cyan for better tag readability
    '#E17055', // Orange - good contrast
    '#9B59B6', // Purple variant - good contrast
    '#2F3542', // Dark gray - good contrast
    '#FF3838', // Red - good contrast
    '#6C5B7B', // Mauve - good contrast
    '#C44569', // Berry - good contrast
    '#F8B500', // Amber - good contrast
    '#5758BB'  // Deep purple - good contrast
];

/**
 * Convert a hex color to RGBA format
 * @param {string} hex - Hex color code (e.g., '#ff0000' or '#f00')
 * @param {number} alpha - Alpha value between 0 and 1 (default: 1)
 * @returns {string} RGBA color string (e.g., 'rgba(255, 0, 0, 1)')
 *
 * @example
 * hexToRGBA('#ff0000', 0.5) // Returns: 'rgba(255, 0, 0, 0.5)'
 * hexToRGBA('#f00', 0.5)    // Returns: 'rgba(255, 0, 0, 0.5)'
 */
export function hexToRGBA(hex = '', alpha = 1) {
    if (!hex) return '';
    const cleaned = hex.replace('#', '').trim();
    const normalized = cleaned.length === 3
        ? cleaned.split('').map((char) => char + char).join('')
        : cleaned;
    if (normalized.length < 6) return '';
    const values = [0, 2, 4].map((offset) => parseInt(normalized.substr(offset, 2), 16) || 0);
    return `rgba(${values[0]}, ${values[1]}, ${values[2]}, ${alpha})`;
}
